// src/pages/admin/StudentsPerformance.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, Search, ChevronDown, ChevronLeft, ChevronRight, Loader2, BookOpen, File } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
// Import jspdf-autotable to extend jsPDF with autoTable method
// This must be imported as a side effect to extend the jsPDF prototype
import 'jspdf-autotable';
import { API_BASE_URL } from '../constants';
import { FaFileExcel, FaFilePdf } from 'react-icons/fa';

const StudentsPerformance = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const classUuid = searchParams.get('class');
  const level = searchParams.get('level');
  const session = searchParams.get('session');
  const termsParam = searchParams.get('terms');
  const selectedTerms = termsParam ? termsParam.split(',') : [];

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({ student: '', term: '', subjects: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [className, setClassName] = useState('');

  useEffect(() => {
    const fetchPerformance = async () => {
      if ((!classUuid && !level) || !session || selectedTerms.length === 0) {
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('authToken');
        const terms = selectedTerms.join(',');
        const params = new URLSearchParams({
          session,
          terms
        });
        
        if (classUuid) {
          params.append('class', classUuid);
        } else if (level) {
          params.append('level', level);
        }

        const res = await fetch(
          `${API_BASE_URL}/api/web/admin/students-performance?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (data.success) {
          setStudents(data.students);
          setClassName(data.className || '');
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPerformance();
  }, [classUuid, level, session, termsParam]);

  const filteredAndSorted = useMemo(() => {
    let filtered = students.filter(s =>
      `${s.lastname} ${s.firstname} ${s.middlename || ''}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.admission_no && s.admission_no.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    if (sortBy) {
      const [term, direction] = sortBy.split(' ');
      const isAsc = direction === 'asc';
      filtered.sort((a, b) => {
        let scoreA = 0, scoreB = 0;
        if (term === 'All') {
          scoreA = a.aggregate_score || 0;
          scoreB = b.aggregate_score || 0;
        } else {
          const key = term.toLowerCase().replace(' ', '_') + '_score';
          scoreA = a[key] || 0;
          scoreB = b[key] || 0;
        }
        return isAsc ? scoreA - scoreB : scoreB - scoreA;
      });
    }

    return filtered;
  }, [students, searchQuery, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSorted.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedStudents = filteredAndSorted.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortBy, itemsPerPage]);

  const exportExcel = () => {
    const data = filteredAndSorted.map(s => ({
      'Student Name': `${s.lastname} ${s.firstname} ${s.middlename || ''}`.trim(),
      'Admission No': s.admission_no || 'N/A',
      'Class': s.classroom_name || className || 'N/A',
      ...(selectedTerms.includes('First Term') ? { 'First Term': s.first_term_score || 'N/A' } : {}),
      ...(selectedTerms.includes('Second Term') ? { 'Second Term': s.second_term_score || 'N/A' } : {}),
      ...(selectedTerms.includes('Third Term') ? { 'Third Term': s.third_term_score || 'N/A' } : {}),
      'All Terms Score': s.aggregate_score || 'N/A',
      'Status': s.status || 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Performance');
    XLSX.writeFile(wb, `${className || 'Performance'} - ${session}.xlsx`);
  };

  const exportPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text(`${className || 'Performance'} - Performance Report ${session}`, 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 22);
      
      const tableData = filteredAndSorted.map(s => {
        const row = [
          `${s.lastname} ${s.firstname} ${s.middlename || ''}`.trim(),
          s.admission_no || '-'
        ];
        
        if (selectedTerms.includes('First Term')) {
          row.push(s.first_term_score ? parseFloat(s.first_term_score).toFixed(2) : '-');
        }
        if (selectedTerms.includes('Second Term')) {
          row.push(s.second_term_score ? parseFloat(s.second_term_score).toFixed(2) : '-');
        }
        if (selectedTerms.includes('Third Term')) {
          row.push(s.third_term_score ? parseFloat(s.third_term_score).toFixed(2) : '-');
        }
        
        row.push(s.aggregate_score ? parseFloat(s.aggregate_score).toFixed(2) : '-');
        row.push(s.status || '-');
        
        return row;
      });

      const headers = ['Name', 'Admission No'];
      if (selectedTerms.includes('First Term')) headers.push('First Term');
      if (selectedTerms.includes('Second Term')) headers.push('Second Term');
      if (selectedTerms.includes('Third Term')) headers.push('Third Term');
      headers.push('All Terms', 'Status');

      // Check if autoTable is available
      if (typeof doc.autoTable === 'function') {
        doc.autoTable({
          startY: 28,
          head: [headers],
          body: tableData,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [66, 139, 202] }
        });
      } else {
        // Fallback: Create table manually
        let yPos = 28;
        const lineHeight = 7;
        const colWidths = [60, 30, ...(selectedTerms.length > 0 ? Array(selectedTerms.length).fill(25) : []), 30, 20];
        let xPos = 14;
        
        // Headers
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        headers.forEach((header, i) => {
          doc.text(header, xPos, yPos);
          xPos += colWidths[i] || 30;
        });
        
        yPos += lineHeight;
        doc.setDrawColor(200, 200, 200);
        doc.line(14, yPos, 200, yPos);
        yPos += 3;
        
        // Data rows
        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        tableData.forEach((row, rowIndex) => {
          if (yPos > 280) {
            doc.addPage();
            yPos = 20;
          }
          xPos = 14;
          row.forEach((cell, i) => {
            doc.text(String(cell), xPos, yPos);
            xPos += colWidths[i] || 30;
          });
          yPos += lineHeight;
        });
      }
      
      doc.save(`${className || 'Performance'}-${session}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please make sure jspdf-autotable is installed.');
    }
  };

  const openSubjectsModal = (student, term) => {
    const termKey = term.toLowerCase().replace(' ', '_') + '_subjects';
    setModalData({
      student: `${student.lastname} ${student.firstname} ${student.middlename || ''}`.trim(),
      term,
      subjects: student[termKey] || [],
      admissionNo: student.admission_no
    });
    setShowModal(true);
  };

   // Utility function to format decimal values
const formatScore = (value) => {
  if (value === undefined || value === null || value === '') return 0;
  
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  
  // If the number ends with .0, show as integer
  if (num % 1 === 0) {
    return Math.floor(num);
  }
  
  // Otherwise, show as is
  return num;
};

  const getTermRoute = (term) => {
    if (term === 'First Term') return 'first-term';
    if (term === 'Second Term') return 'second-term';
    if (term === 'Third Term') return 'third-term';
    return term.toLowerCase().replace(' ', '-');
  };

//   console.log({paginatedStudents});
  

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold">Students Performance Report</h1>
        <p className="text-blue-100 mt-2">
          {className || 'Class Level'} • {session} • {selectedTerms.join(', ')}
        </p>
        <p className="text-blue-200 text-sm mt-1">
          Total Students: {filteredAndSorted.length} | Showing {startIndex + 1}-{Math.min(endIndex, filteredAndSorted.length)} of {filteredAndSorted.length}
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search student..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-lg dark:bg-gray-700"
              />
            </div>

            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
            >
              <option value="">Sort by performance...</option>
              {selectedTerms.includes('First Term') && (
                <>
                  <option value="First Term asc">First Term (Low → High)</option>
                  <option value="First Term desc">First Term (High → Low)</option>
                </>
              )}
              {selectedTerms.includes('Second Term') && (
                <>
                  <option value="Second Term asc">Second Term (Low → High)</option>
                  <option value="Second Term desc">Second Term (High → Low)</option>
                </>
              )}
              {selectedTerms.includes('Third Term') && (
                <>
                  <option value="Third Term asc">Third Term (Low → High)</option>
                  <option value="Third Term desc">Third Term (High → Low)</option>
                </>
              )}
              <option value="All asc">All Terms (Low → High)</option>
              <option value="All desc">All Terms (High → Low)</option>
            </select>

            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                Per page:
              </label>
              <select
                value={itemsPerPage}
                onChange={e => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white text-sm"
              >
                {[100, 200, 300, 400, 500, 600, 700, 800, 900, 1000].map(num => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2">
              <button onClick={exportExcel} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                <FaFileExcel className="w-4 h-4" /> Excel
              </button>
              <button onClick={exportPDF} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                <FaFilePdf className="w-4 h-4" /> PDF
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Student Name</th>
                {selectedTerms.includes('First Term') && (
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">First Term</th>
                )}
                {selectedTerms.includes('Second Term') && (
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Second Term</th>
                )}
                {selectedTerms.includes('Third Term') && (
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Third Term</th>
                )}
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">All Terms Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedStudents.length === 0 ? (
                <tr>
                  <td colSpan={selectedTerms.length + 2} className="px-6 py-8 text-center text-gray-500">
                    No students found
                  </td>
                </tr>
              ) : (
                paginatedStudents.map((s, i) => (
                  <tr key={s.id || s.uuid || i} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {s.lastname} {s.middlename || ''} {s.firstname || ''}
                        </div>
                        {s.admission_no && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">{s.admission_no}</div>
                        )}
                        {s.classroom_name && (
                          <div className="text-xs text-gray-400 dark:text-gray-500">{s.classroom_name}</div>
                        )}
                      </div>
                    </td>
                    {selectedTerms.includes('First Term') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col items-center gap-2">
                          <span className={`font-semibold text-lg ${
                            s.first_term_score >= 70 ? 'text-green-600' : 
                            s.first_term_score >= 50 ? 'text-blue-600' : 
                            s.first_term_score ? 'text-red-600' : 'text-gray-400'
                          }`}>
                            {s.first_term_score ? formatScore(s.first_term_score) : '-'}
                          </span>
                          <div className="flex items-center gap-2">
                            {s.first_term_subjects && s.first_term_subjects.length > 0 && (
                              <button
                                onClick={() => openSubjectsModal(s, 'First Term')}
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                title="View Subject Scores"
                              >
                                <BookOpen className="w-4 h-4" />
                              </button>
                            )}
                            {s.first_term_score && (
                              <button
                                onClick={() => navigate(`/student-single-result?studentUuid=${s.uuid}&className=${s.classroom_name || className}&session=${session}&term=first_term`)}
                                className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded hover:bg-blue-200 dark:hover:bg-blue-800"
                              >
                                View Result
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                    )}
                    {selectedTerms.includes('Second Term') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col items-center gap-2">
                          <span className={`font-semibold text-lg ${
                            s.second_term_score >= 70 ? 'text-green-600' : 
                            s.second_term_score >= 50 ? 'text-blue-600' : 
                            s.second_term_score ? 'text-red-600' : 'text-gray-400'
                          }`}>
                            {s.second_term_score ? formatScore(s.second_term_score) : '-'}
                          </span>
                          <div className="flex items-center gap-2">
                            {s.second_term_subjects && s.second_term_subjects.length > 0 && (
                              <button
                                onClick={() => openSubjectsModal(s, 'Second Term')}
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                title="View Subject Scores"
                              >
                                <BookOpen className="w-4 h-4" />
                              </button>
                            )}
                            {s.second_term_score && (
                              <button
                                onClick={() => navigate(`/student-single-result?studentUuid=${s.uuid}&className=${s.classroom_name || className}&session=${session}&term=second_term`)}
                                className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded hover:bg-blue-200 dark:hover:bg-blue-800"
                              >
                                View Result
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                    )}
                    {selectedTerms.includes('Third Term') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col items-center gap-2">
                          <span className={`font-semibold text-lg ${
                            s.third_term_score >= 70 ? 'text-green-600' : 
                            s.third_term_score >= 50 ? 'text-blue-600' : 
                            s.third_term_score ? 'text-red-600' : 'text-gray-400'
                          }`}>
                            {s.third_term_score ? formatScore(s.third_term_score) : '-'}
                          </span>
                          <div className="flex items-center gap-2">
                            {s.third_term_subjects && s.third_term_subjects.length > 0 && (
                              <button
                                onClick={() => openSubjectsModal(s, 'Third Term')}
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                title="View Subject Scores"
                              >
                                <BookOpen className="w-4 h-4" />
                              </button>
                            )}
                            {s.third_term_score && (
                              <button
                                onClick={() => navigate(`/student-single-result?studentUuid=${s.uuid}&className=${s.classroom_name || className}&session=${session}&term=third_term`)}
                                className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded hover:bg-blue-200 dark:hover:bg-blue-800"
                              >
                                View Result
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex flex-col items-center gap-2">
                        <div className="font-bold text-xl text-gray-900 dark:text-white">
                          {s.aggregate_score ? formatScore(s.aggregate_score) : '-'}
                        </div>
                        {s.status && (
                          <span className={`text-xs px-2 py-1 rounded ${
                            s.status === 'passed' 
                              ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' 
                              : 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300'
                          }`}>
                            {s.status.toUpperCase()}
                          </span>
                        )}
                        {s.aggregate_score && (
                          <button
                            onClick={() => navigate(`/student-single-result?studentUuid=${s.uuid}&className=${s.classroom_name || className}&session=${session}&term=combined`)}
                            className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-3 py-1 rounded hover:bg-green-200 dark:hover:bg-green-800 font-medium"
                          >
                            View Combined
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Subject Scores Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {modalData.student} - {modalData.term} Subject Scores
                </h3>
                {modalData.admissionNo && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Admission No: {modalData.admissionNo}
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            {modalData.subjects.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                No subject scores available for this term
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Subject</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Score</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Grade</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {modalData.subjects.map((sub, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                          {sub.subject_name}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white">
                          {sub.total_score ? parseFloat(sub.total_score).toFixed(2) : '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            sub.grade === 'A' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                            sub.grade === 'B' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                            sub.grade === 'C' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                            sub.grade === 'D' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300' :
                            'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                          }`}>
                            {sub.grade || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {modalData.subjects.length > 0 && (
                    <tfoot className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <td className="px-4 py-3 font-bold text-gray-900 dark:text-white">Total</td>
                        <td className="px-4 py-3 text-right font-bold text-gray-900 dark:text-white">
                          {modalData.subjects.reduce((sum, sub) => sum + (parseFloat(sub.total_score) || 0), 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3"></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentsPerformance;